import React, { useState, useEffect } from "react";
import { Bell, Settings, Plus, Phone, Wifi, Zap, Tv, GraduationCap } from "lucide-react";
import { motion, AnimatePresence, wrap } from "framer-motion";
import { useNavigate } from "react-router-dom";

import MobileBottomNav from "../../components/feature/MobileBottomNav";
import vec1 from "../../assets/images/vector1.png";
import vec2 from "../../assets/images/vector2.png";
import vec3 from "../../assets/images/vector3.png";
import vec4 from "../../assets/images/vector4.png";
import verycard from "../../assets/images/verifyimg.png";
import anotherCard from "../../assets/images/rewardimg.png";

import { getMyWallet } from "../../api/apiServices";
import BannerSlider from "./components/BannerSlider";
import TopDataOffersSection from "../../components/TopDataOffersSection";
import { useProfileCompletion } from "../../hooks/useProfileCompletion";
import { useAuth } from "../../context/useAuth";

const Dashboard = () => {
  const navigate = useNavigate();

  // ── Wallet Balance ───────────────────────────────────────
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [balanceError, setBalanceError] = useState(null);
  const { user } = useAuth();

  const { isComplete, missingFields } = useProfileCompletion(user);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        setLoadingBalance(true);
        const wallet = await getMyWallet();

        if (wallet && typeof wallet.balance === "number") {
          setBalance(wallet.balance);
        } else {
          setBalance(0);
        }
      } catch (err) {
        console.error("Failed to fetch wallet:", err);
        setBalanceError("Could not load balance");
        setBalance(0);
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalance();
  }, []);

  // Format balance (₦1,234,567.00)
  const formattedBalance = balance?.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const handleOfferSelect = (offer) => {
    navigate('/buy-bundle', {
      state: {
        selectedOffer: offer,
        openPlanModal: true
      }
    });
  };

  // ── Unified Actions Config ──────────────────────────────
  const mainActions = [
    { nav: "/buy-airtime", vecA: vec1, vecB: vec2, title: "Buy Airtime" },
    { nav: "/buy-bundle", vecA: vec3, vecB: vec4, title: "Buy Bundle" },
  ];

  const billActions = [
    { nav: "/pay-electricity", icon: Zap, title: "Electricity", color: "text-amber-500", bg: "bg-amber-50" },
    { nav: "/pay-cable", icon: Tv, title: "Cable TV", color: "text-purple-500", bg: "bg-purple-50" },
    { nav: "/buy-epin", icon: GraduationCap, title: "Exam PIN", color: "text-emerald-500", bg: "bg-emerald-50" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-[#3866a3]">Dashboard</h1>
        </div>

        <div className="flex items-center gap-2">
          <a
            href="/account"
            className="p-2 rounded-full hover:bg-gray-100 transition"
          >
            <Settings size={20} className="text-gray-600" />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pt-6 space-y-7 max-w-md mx-auto">
        {/* Balance Card */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow border p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600 font-medium">Total balance</p>
            <a href="/add-fund">
              <button className="bg-[#3866A3] text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#2f5590] transition">
                <Plus size={16} /> Add cash
              </button>
            </a>
          </div>

          {loadingBalance ? (
            <div className="text-3xl font-bold text-gray-400">Loading...</div>
          ) : balanceError ? (
            <div className="text-3xl font-bold text-red-600">
              Error loading balance
            </div>
          ) : (
            <div className="text-[32px] font-bold text-[#0F172A]">
              ₦{formattedBalance || "0.00"}
            </div>
          )}
        </motion.section>

        {/* Primary Actions (Airtime & Bundles) */}
        <section className="grid grid-cols-2 gap-4">
          {mainActions.map((action, i) => (
            <motion.button
              key={i}
              onClick={() => navigate(action.nav)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="bg-[#E9EDF6] border border-gray-200 rounded-[16px] overflow-hidden shadow-[0_10px_20px_-10px_rgba(0,0,0,0.18)] transition-all duration-300 ease-out text-left"
            >
              <div className="px-4 pt-3 pb-1 font-semibold text-[19px] text-[#3866A3]">
                {action.title}
              </div>
              <div className="relative h-20">
                <img
                  src={action.vecA}
                  alt=""
                  className="absolute bottom-0 left-0 h-14 w-20 object-contain"
                />
                <img
                  src={action.vecB}
                  alt=""
                  className="absolute bottom-3 right-1 h-10 w-16 object-contain drop-shadow-sm"
                />
              </div>
            </motion.button>
          ))}
        </section>

        {/* Utilities & Bill Payments Section */}
        <section className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
            Pay Bills & Utilities
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {billActions.map((bill, i) => {
              const IconComponent = bill.icon;
              return (
                <motion.button
                  key={i}
                  onClick={() => navigate(bill.nav)}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center justify-center p-3 rounded-xl hover:bg-gray-50 transition border border-transparent active:border-gray-100"
                >
                  <div className={`w-12 h-12 rounded-full ${bill.bg} flex items-center justify-center mb-2 shadow-sm`}>
                    <IconComponent className={`w-6 h-6 ${bill.color}`} />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 text-center leading-tight">
                    {bill.title}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </section>

        {/* Carousel */}
        <BannerSlider />

        {/* Top Data Offers */}
        <div className="mt-8 px-4 mb-6">
          <TopDataOffersSection
            title="Top Data Offers"
            maxItems={6}
            onOfferPress={handleOfferSelect}
            containerStyle="mt-4"
          />
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default Dashboard;